/**
 * Defines an object that serves as a container for properties associated with a group of UIContainer documents
 * identified by a document type.  These properties may be fixed, meaning that they should be set at construction
 * time and not changed or dynamic meaning that they can change to reflect and control the UIContainer's management
 * of the documents sharing the type.  By extending "Stateful" this class inherits get, set and watch methods that
 * can be used to manage dynamic properties.
 *
 * @module MW/uiframework/DocumentTypeProperties
 * @copyright Copyright 2015-2020 The MathWorks, Inc.
 */
define([
    'dojo/_base/declare',
    'dojo/_base/lang',
    'MW/layout/DocumentGroupProperties',
    'MW/uiframework/uicontainer/WindowUtils',
    'mw-log/Log'
], function (declare, lang, DocumentGroupProperties, WindowUtils, Log) {
    /**
     * @class MW/uiframework/DocumentTypeProperties
     * @classdesc Defines an object to hold properties associated with a UIContainer document type
     */
    var DocumentTypeProperties = declare([DocumentGroupProperties], {

        /**
         * @property {boolean}  isDockable            Controls whether documents of this type can be docked by the user.  By default docking is allowed.
         *                                            Setting this property to false does not prevent programmatic docking.
         * @property {boolean} suppressInitialFocus   If true then document's of the type will not be given focus when opened
         * @property {boolean} willReopen             Indicates whether documents of the type are expected to reopen in the next session
         * @property {Array}   tabGroupTags           Strings identifying the tab groups to be included on the toolstrip
         *                                            when a document of the type is selected
         * @property {Array}   statusComponentTags    Strings identifying components that should appear on the status bar when a document
         *                                            of the type is selected.
         * @property {Array}   panelIds               Strings identifying the panels to be shown when a document of the type is selected
         * @property {Array}   contexts               An array containing objects that define sub-contexts within documents of the given type.
         *                                            Each sub-context may have associated toolstrip tab groups as well as associated panels.
         *                                            Each object has the form
         *                                            {
         *                                                contextId: String,
         *                                                tabGroupTags: [],
         *                                                statusComponentTags: []
         *                                                panelIds: [],
         *                                            }, where all but the contextId properties are optional.
         *                                            The contextIds identify sub-contexts that exist within documents of the given type.
         *                                            Document sub-contexts are activated by setting the activeContexts property
         *                                            on an individual document (see ChildProperties).
         * @property {Object}   qabControlToActionMap Maps quick access toolbar control tags to corresponding action ids
         * @property {UIComponentFactory} documentFactory  Factory that constructs documents of the given type from saved state.
         *                                            Required if document undocking is to be supported.
         * @property {Object}   tabGroupFactories     Maps tab group tags to factories (of subclass UIComponentFactory) that produce
         *                                            the corresponding tab group.  There must be an entry for each of the tags
         *                                            identified in either the tabGroupTags array or the contexts array.
         * @property {Object}  statusComponentFactory A subclass of UIComponentFactory that creates the components corresponding to any
         *                                            statusComponentTags provided (including those within the contexts array).
         * @instance
         */
        Fixed: undefined, // Trick to make the JSDoc generator list the above under "Fixed"

        /**
         * @property {boolean}  isDocked             Reflects/controls whether documents of the given type are currently docked
         * @property {{x: number, y: number, w: number, h: number}} windowBounds
         *                                           Reflects/controls the position and size of the undocked window containing
         *                                           documents of the type.  If there is no such window, for example if documents of
         *                                           the type are currently docked, then the value does not reflect any current
         *                                           window state but will be used in the event of future undocking.
         *                                           For each of the constituent numeric values, if the value is 0 or >= 1 pixels are assumed.
         *                                           If 0 < value < 1 the value is assumed to be a fraction of the screen size.  Finally if the
         *                                           value is < 0 a default will be substituted upon use.
         * @property {String} origin                 Reflects/controls whether windowBounds and bounds are reported from a bottom-left or top-left
         *                                           point of reference.  By default origin is set to top-left.  Use DocumentTypeProperties.BOTTOM_LEFT_ORIGIN
         *                                           and DocumentTypeProperties.TOP_LEFT_ORIGIN constant values to set origin property.
         * @instance
         */
        Dynamic: undefined, // Trick to make the JSDoc generator list the above under "Dynamic"

        isDocked: true,

        /**
         * Creates a DocumentTypeProperties instance
         *
         * @param initialProperties  an object containing properties that will be mixed into the constructed object
         * @instance
         */
        constructor: function (initialProperties) {
            delete this.Fixed; // Not a real property just a JSDoc trick
        },

        /**
         * Returns true if documents of this type can be undocked.  The default implementation will return true if
         * the factories required for reproducing documents and tabGroups have been supplied.
         *
         * @instance
         */
        isUndockable: function () {
            var i, tag;

            // Make sure there is a document factory
            if (!this.documentFactory || !this.canUndock) { // 'canUndock' is a temporary solution (20b only) to be replaced by 'dynamicDockability'
                return false;
            }
            // Make sure there is a factory for each tab group
            if (this.tabGroupTags && this.tabGroupTags.length > 0) {
                if (!this.tabGroupFactories) {
                    return false;
                }
                for (i = 0; i < this.tabGroupTags.length; i++) {
                    tag = this.tabGroupTags[i];
                    if (!this.tabGroupFactories[tag]) {
                        return false;
                    }
                }
            }
            // TODO: check for StatusBar and Panels as well
            return true;
        }
    });

    // Constant name definitions for dynamic properties
    DocumentTypeProperties.DOCUMENT_COUNT = DocumentGroupProperties.DOCUMENT_COUNT;
    DocumentTypeProperties.FACET = 'documentType';
    DocumentTypeProperties.IS_DOCKED = 'isDocked';
    DocumentTypeProperties.WINDOW_BOUNDS = 'windowBounds';
    DocumentTypeProperties.ORIGIN = 'origin';
    DocumentTypeProperties.IS_MAXIMIZED = DocumentGroupProperties.IS_MAXIMIZED;
    DocumentTypeProperties.IS_OPEN = DocumentGroupProperties.IS_OPEN;
    DocumentTypeProperties.LAST_SELECTED = DocumentGroupProperties.LAST_SELECTED;
    DocumentTypeProperties.TILE = DocumentGroupProperties.TILE;
    DocumentTypeProperties.SUB_GRID_SIZE = DocumentGroupProperties.SUB_GRID_SIZE;
    DocumentTypeProperties.SHRINK_TABS_TO_FIT = DocumentGroupProperties.SHRINK_TABS_TO_FIT;

    // Constant name definitions for enum properties
    DocumentTypeProperties.BOTTOM_LEFT_ORIGIN = WindowUtils.BOTTOM_LEFT_ORIGIN;
    DocumentTypeProperties.TOP_LEFT_ORIGIN = WindowUtils.TOP_LEFT_ORIGIN;

    return DocumentTypeProperties;
});
